<template>
  <section>
    <!-- ── Sin configurar o sin respuesta: se dice y no se rompe nada ── -->
    <div v-if="fatal" class="card card-p">
      <div class="c-lbl" style="margin-bottom:8px">Radar COMEX</div>
      <p style="margin:0 0 4px">{{ fatal }}</p>
      <p style="margin:0;color:var(--tx-lo);font-size:13px">
        Es un sistema aparte: mientras no responda, el resto de MHLM funciona igual.
      </p>
    </div>

    <!-- ── Las dos solapas del módulo ──────────────────────────────────
         Publicaciones es el trabajo de todos los días; Configuración es lo
         que se toca cada tanto: suscriptores, visitas, horarios, remitente.
         La configuración se pide recién al abrir la solapa — quien entra a
         aprobar piezas no paga ese viaje. -->
    <template v-else>
      <div style="display:flex;gap:22px;border-bottom:1px solid var(--border);margin-bottom:18px">
        <button :style="estiloSolapa('publicaciones')" @click="solapa = 'publicaciones'">Publicaciones</button>
        <button :style="estiloSolapa('configuracion')" @click="abrirConfiguracion">Configuración</button>
      </div>

    <!-- ── El día: qué hay para publicar ─────────────────────────────
         Una sola pantalla. Antes había dos —el día, y la ficha de la pieza
         detrás de un botón «Abrir para publicar»— y eso obligaba a ir y
         volver para hacer una sola cosa: «¿para qué el botón abrir para
         publicar? Ahí mismo todo». -->
    <div v-if="solapa === 'publicaciones'">
      <!--
        La barra del día, chica y a un costado.
        Antes ocupaba el frente con cuatro tarjetas de métricas —avisos leídos,
        con impacto, descartados, sin filtrar— que no cambian nada de lo que se
        hace acá: «¿para qué está todo esto que no sirve para nada?». El
        selector queda por lo único que sirve: mirar los días anteriores.
      -->
      <div class="dia-barra">
        <span class="dia-lbl">Día</span>
        <select v-model="fecha" class="fi" style="max-width:230px; padding:4px 10px; font-size:12px"
                @change="cargarDia">
          <option v-for="d in dias" :key="d.fecha" :value="d.fecha">{{ formatoFecha(d.fecha) }}</option>
        </select>
        <button class="btn btn-g btn-sm" :disabled="cargando" @click="cargarDia">Actualizar</button>

        <!-- Lo que ya salió al aire ese día: chiquito y de referencia, para
             saber que está hecho sin que ocupe la lista de lo que falta.

             Va pegado al selector y con la frase entera adentro del mismo
             botón. Suelto a la derecha y partido en dos —un rótulo acá, el
             título allá— no se entendía qué era ese texto. -->
        <span v-if="publicadasHoy.length" class="dia-ya">
          <button v-for="p in publicadasHoy" :key="p.clave" class="dia-ya-it"
                  :class="{ 'is-sel': p.clave === elegida }" @click="elegir(p.clave)"
                  :title="'Ver la publicación: ' + p.titulo">
            ✓ Ya publicaste: {{ p.titulo }}
          </button>
        </span>
      </div>

      <div class="pub-split">
        <!-- ── Columna izquierda: lo del día y la biblioteca entera ── -->
        <div class="pub-col">
          <!--
            La lista aparece SOLO cuando hay más de una pieza pendiente, que
            es cuando sirve para algo: elegir.

            Con una sola repetía el título, el estado y la serie que ya están
            a la derecha, en grande —«¿para qué está?»—; y con ninguna era un
            recuadro que decía que estaba vacío. La biblioteca, abajo, es lo
            único que se puede hacer en esos dos casos.
          -->
          <div v-if="pendientes.length > 1" class="card card-p">
            <div class="pub-items">
              <button v-for="p in pendientes" :key="p.clave"
                      class="pub-item" :class="{ 'is-sel': p.clave === elegida }"
                      @click="elegir(p.clave)">
                <div class="pub-item-t">{{ p.titulo }}</div>
                <div class="pub-item-m">
                  <span class="pill" :class="p.listo ? 'pill-b' : 'pill-a'">
                    {{ p.listo ? 'para publicar' : 'sin placas' }}
                  </span>
                  <span class="pub-item-e">{{ p.es_biblioteca ? serieLegible(p.etiqueta) : p.etiqueta }}</span>
                </div>
              </button>
            </div>
          </div>

          <!-- ── La biblioteca, entera y a la vista ──────────────────
               Era un combo desplegable: «no quiero un combo para cambiar».
               Un combo esconde 60 temas atrás de un click y no deja
               comparar; acá está la lista completa, se toca uno y se arma. -->
          <div v-if="biblioteca" class="card card-p" style="margin-top:16px">
            <div style="display:flex;gap:8px;align-items:baseline;flex-wrap:wrap;margin-bottom:10px">
              <span class="c-lbl">La biblioteca</span>
              <span class="pill pill-s">{{ temasFiltrados.length }} para publicar</span>
            </div>
            <p style="margin:0 0 10px;color:var(--tx-lo);font-size:12px">
              Tocá un tema y se arma la publicación de este día. El ★ es el interés que
              estimamos para el rubro, sobre 25.
            </p>

            <!-- El buscador arriba de todo, con las sugerencias de los títulos
                 que hay: escribiendo dos palabras el navegador completa el
                 resto, y no hace falta acordarse del título entero. -->
            <input v-model="filtroTema" class="fi" list="temas-biblioteca" type="search"
                   placeholder="Buscar por título o serie…"
                   style="width:100%;padding:5px 12px;font-size:12px;margin-bottom:10px">
            <datalist id="temas-biblioteca">
              <option v-for="t in temasVisibles" :key="t.id" :value="t.titulo"></option>
            </datalist>

            <!-- Lo ya publicado se esconde: la lista es para elegir qué sacar,
                 no un archivo. Sigue estando, a un click. -->
            <div v-if="temasPublicados" style="margin:0 0 10px">
              <button class="btn btn-g btn-sm" @click="verPublicadas = !verPublicadas">
                {{ verPublicadas
                  ? `Ocultar las ${temasPublicados} ya publicadas`
                  : `Ver también las ${temasPublicados} ya publicadas` }}
              </button>
            </div>

            <!-- Repetir un tema reciente se puede, pero se pregunta. -->
            <div v-if="repite" class="pub-repite">
              <div style="color:var(--tx-hi);font-weight:600;font-size:12.5px">{{ repite.tema.titulo }}</div>
              <p style="margin:6px 0 10px;font-size:12.5px;color:var(--tx-md)">{{ repite.motivo }}</p>
              <div style="display:flex;gap:8px;flex-wrap:wrap">
                <button class="btn btn-sm" @click="armarTema(repite.tema, true)">Publicarla igual</button>
                <button class="btn btn-g btn-sm" @click="repite = null">Dejarla</button>
              </div>
            </div>

            <div class="pub-items pub-items-alta">
              <button v-for="t in temasFiltrados" :key="t.id"
                      class="pub-item" :disabled="!!creando" @click="armarTema(t)">
                <div class="pub-item-t">{{ t.titulo }}</div>
                <div class="pub-item-m">
                  <!-- La clasificación, no sólo el número: «★ 24» no dice nada
                       si no se sabe contra qué se compara. -->
                  <span v-if="t.interes" class="pub-estrella" :class="'int-' + nivelInteres(t.interes).grado">
                    ★ {{ t.interes }} · {{ nivelInteres(t.interes).texto }}
                  </span>
                  <span class="pub-item-e">{{ serieLegible(t.serie) }}</span>
                  <span v-if="t.publicada_el" class="pill pill-g">
                    publicada el {{ t.publicada_el.split('-').reverse().join('/') }}
                  </span>
                  <span v-else-if="t.es_el_del_dia" class="pill pill-b">es la de hoy</span>
                </div>
              </button>
              <div v-if="!temasFiltrados.length" class="tbl-empty">
                {{ filtroTema ? 'Ningún tema con ese texto.' : 'Todos los temas ya salieron al aire.' }}
              </div>
            </div>
          </div>
        </div>

        <!-- ── Columna derecha: la publicación, entera ───────────────
             Placas, textos, copiar, el ZIP y «ya la publiqué». Todo acá: no
             hay otra pantalla.

             Sin publicación elegida no hay columna: un recuadro que dice
             «nada para mostrar» es exactamente eso, nada, ocupando media
             pantalla. Los días sin nada quedan con la biblioteca sola, que
             es lo único que se puede hacer ahí. -->
        <div v-if="publicacion" class="pub-detalle">
            <div class="card card-p mb-16">
              <div style="display:flex;gap:8px;align-items:baseline;flex-wrap:wrap;margin-bottom:8px">
                <span class="pill" :class="publicacion.es_biblioteca ? 'pill-b' : 'pill-s'">
                  {{ publicacion.es_biblioteca ? serieLegible(publicacion.etiqueta) : publicacion.etiqueta }}
                </span>
                <!-- El puntaje que le estimamos, en la cabecera: es el dato
                     con el que se decidió publicarla, y hasta acá había que
                     buscarlo de nuevo en la lista de la biblioteca. -->
                <span v-if="publicacion.interes" class="pub-estrella"
                      :class="'int-' + nivelInteres(publicacion.interes).grado"
                      :title="'Interés estimado para el rubro, sobre 25'">
                  ★ {{ publicacion.interes }} · {{ nivelInteres(publicacion.interes).texto }}
                </span>
                <span v-if="publicacion.publicada" class="pill pill-g">✓ publicada</span>
                <span v-else class="pill pill-a">{{ publicacion.estado }}</span>
                <!-- La verificación de citas es de las piezas normativas. Una
                     pieza permanente no cita ninguna norma, así que marcarla
                     «sin verificar» en rojo señalaba un problema inexistente. -->
                <span v-if="ficha && !ficha.evergreen" class="pill"
                      :class="ficha.verificacion === 'ok' ? 'pill-g' : 'pill-r'">
                  citas {{ ficha.verificacion || 'sin verificar' }}
                </span>
                <span v-if="ficha?.evergreen && !ficha.evergreen.validado" class="pill pill-a">primera vez</span>
              </div>

              <h2 style="margin:0;font-size:17px;line-height:1.3">{{ publicacion.titulo }}</h2>

              <a v-if="ficha?.norma?.url_oficial" :href="ficha.norma.url_oficial" target="_blank"
                 rel="noopener" class="btn btn-g btn-sm" style="margin-top:12px">Ver la norma en el Boletín ↗</a>

              <!-- Las placas, del tamaño en que se miran de verdad. Una imagen
                   rota no se disimula: si falta, lo dice. -->
              <div v-if="publicacion.placas.length" class="pub-placas">
                <div v-for="(url, i) in publicacion.placas" :key="url" class="pub-placa">
                  <img :src="url" :alt="`Placa ${i + 1}`" loading="lazy"
                       @error="rotas[publicacion.clave + '-' + i] = true">
                  <div v-if="rotas[publicacion.clave + '-' + i]" class="pub-placa-falta">
                    Esta placa todavía no se generó.
                  </div>
                  <span class="pub-placa-n">{{ i + 1 }}/{{ publicacion.placas.length }}</span>
                </div>
              </div>
              <div v-else class="tbl-empty" style="text-align:left;margin-top:12px">
                Todavía no tiene placas: se generan en la próxima corrida.
              </div>

              <!-- El ZIP, junto a las imágenes que baja. Estaba escondido en un
                   «copiar partes por separado» al final de la pantalla: el
                   botón para llevarse las placas va donde están las placas. -->
              <div v-if="publicacion.placas.length" style="margin-top:14px">
                <button class="btn btn-sm" :disabled="trabajando || !ficha" @click="descargar">
                  {{ trabajando ? 'Preparando…' : `Descargar las ${publicacion.placas.length} placas (ZIP)` }}
                </button>
              </div>
            </div>

            <div v-if="!ficha || ficha.id !== publicacion.ig" class="card card-p tbl-empty">
              Trayendo el texto de la pieza…
            </div>

            <template v-else>
              <div class="card card-p mb-16">
                <div style="display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;margin-bottom:12px">
                  <span class="c-lbl">{{ ficha.canal === 'linkedin' ? 'El análisis' : 'El texto que acompaña' }}</span>
                  <!-- Copiar el texto vive al lado del texto, igual que el ZIP
                       al lado de las placas: cada cosa donde está la cosa. -->
                  <button class="btn btn-g btn-sm" style="margin-left:auto"
                          @click="copiar(textoParaPublicar, 'texto')">
                    {{ copiado === 'texto' ? '✓ Copiado' : 'Copiar este texto' }}
                  </button>
                </div>
                <!-- Se muestra el texto COMPLETO tal como sale publicado: cuerpo,
                     hashtags y el enlace al sitio. Antes acá se veía sólo el cuerpo
                     y parecía que faltaban los # y el link, aunque estaban en lo que
                     se copia. Lo que se revisa tiene que ser lo que se publica. -->
                <pre style="white-space:pre-wrap;font:inherit;margin:0">{{ textoParaPublicar || '—' }}</pre>
              </div>

              <!-- La pieza del otro canal, en la misma pantalla: las placas son
                   las mismas y saltar entre dos fichas hacía perder tiempo. -->
              <div v-if="ficha.hermano" class="card card-p mb-16">
                <div style="display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;margin-bottom:10px">
                  <span class="c-lbl">El texto de {{ ficha.hermano.canal === 'linkedin' ? 'LinkedIn' : 'Instagram' }}</span>
                  <span class="pill pill-b">misma pieza, mismas placas</span>
                  <button class="btn btn-g btn-sm" style="margin-left:auto"
                          @click="copiar(ficha.hermano.texto_completo, 'hermano')">
                    {{ copiado === 'hermano' ? '✓ Copiado' : 'Copiar este texto' }}
                  </button>
                </div>
                <pre style="white-space:pre-wrap;font:inherit;margin:0;color:var(--tx-md);font-size:13.5px">{{ ficha.hermano.texto_completo }}</pre>
              </div>


              <!--
                Publicada: sí o no. Nada más.
                Acá vivían el botón de «preparar» —que bajaba las placas y
                copiaba el texto de una— y un desplegable de «copiar partes por
                separado». Las dos cosas se mudaron a donde está lo que
                manejan: el ZIP junto a las placas, el copiar junto al texto.
              -->
              <div class="card card-p mb-16">
                <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
                  <span class="c-lbl" style="margin-right:4px">¿Ya está publicada?</span>
                  <template v-if="publicacion.publicada">
                    <span class="pill pill-g">✓ sí, ya salió</span>
                  </template>
                  <template v-else>
                    <button v-if="ficha.hermano" class="btn btn-sm" :disabled="marcando" @click="marcarPublicada(true)">
                      {{ marcando ? 'Un momento…' : '✓ Sí, publiqué IG y LinkedIn' }}
                    </button>
                    <button class="btn btn-g btn-sm" :disabled="marcando" @click="marcarPublicada(false)">
                      {{ ficha.hermano ? 'Sólo esta' : (marcando ? 'Un momento…' : '✓ Sí, ya la publiqué') }}
                    </button>
                  </template>
                </div>

                <p v-if="aviso" style="margin:12px 0 0;font-size:13px">{{ aviso }}</p>
              </div>

              <!-- El gate de evidencia es de las piezas normativas: cada
                   afirmación sobre una norma tiene que poder mostrar su cita
                   textual. Una pieza permanente no habla de ninguna norma. -->
              <div v-if="!ficha.evergreen" class="card card-p mb-16">
                <div class="c-lbl" style="margin-bottom:6px">En qué se apoya cada afirmación</div>
                <p style="color:var(--tx-lo);font-size:13px;margin:0 0 12px">
                  Sin esto no se puede aprobar: es la cita textual de la norma.
                </p>
                <div v-if="!ficha.evidencias?.length" class="tbl-empty">Sin evidencias registradas.</div>
                <blockquote v-for="(ev, i) in ficha.evidencias" :key="i"
                            style="margin:0 0 10px;padding-left:12px;border-left:3px solid var(--tx-lo)">
                  <span style="font-size:14px">{{ textoEvidencia(ev) }}</span>
                </blockquote>
              </div>

              <!--
                Acá había dos tarjetas más que se fueron:

                - La que explicaba qué es una pieza permanente. Se lee una vez
                  y después estorba todos los días.
                - «Aprobar y programar» / «Descartar». Sobre una pieza ya
                  publicada mostraba un cartel rojo —«no está en revisión
                  (estado APROBADO)»— y un botón muerto. Y sobre una pendiente
                  tampoco hacía falta: «✓ Sí, ya la publiqué» aprueba sola,
                  con el mismo control de evidencia, y lo que no se quiere
                  publicar simplemente no se publica.
              -->

            </template>
        </div>
      </div>

      <!-- Armar una pieza tarda: se crea el borrador de los dos canales y se
           renderizan las placas ahí mismo. Sin este aviso la pantalla parecía
           colgada y no se sabía si había pasado algo. -->
      <div v-if="creando" class="pub-modal">
        <div class="pub-modal-caja">
          <div class="pub-modal-spin"></div>
          <div class="pub-modal-t">Creando la publicación</div>
          <div class="pub-modal-s">{{ creando }}</div>
          <div class="pub-modal-p">Armando el texto y las placas. Tarda unos segundos.</div>
        </div>
      </div>
    </div>

    <!-- ── Configuración ───────────────────────────────────────────── -->
    <div v-else>
      <!-- El contador de visitas crece solo: sin esto había que salir y
           volver a entrar a la solapa para ver el número nuevo. -->
      <div style="margin-bottom:16px">
        <button class="btn btn-g btn-sm" :disabled="cargandoConfig" @click="cargarConfiguracion">
          {{ cargandoConfig ? 'Un momento…' : 'Actualizar' }}
        </button>
      </div>

      <div v-if="cargandoConfig && !config" class="card card-p tbl-empty">Cargando la configuración…</div>

      <template v-else>
        <!-- Suscriptores -->
        <div class="card card-p mb-16">
          <div style="display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;margin-bottom:12px">
            <span class="c-lbl">Suscriptores del boletín</span>
            <span class="pill pill-g">{{ config?.suscriptores?.activos || 0 }} activos</span>
            <span v-if="config?.suscriptores?.pendientes" class="pill pill-a">{{ config.suscriptores.pendientes }} sin confirmar</span>
            <span v-if="config?.suscriptores?.bajas" class="pill pill-s">{{ config.suscriptores.bajas }} bajas</span>
          </div>

          <div v-if="!config?.suscriptores?.lista?.length" class="tbl-empty">Todavía no hay suscriptores.</div>
          <div v-else>
            <div v-for="s in config.suscriptores.lista" :key="s.email"
                 style="display:flex;gap:12px;align-items:baseline;flex-wrap:wrap;padding:9px 0;
                        border-top:1px solid var(--bd, rgba(128,128,128,.18));font-size:13.5px">
              <span style="min-width:240px;color:var(--tx-hi)">{{ s.email }}</span>
              <span class="pill" :class="{ activo: 'pill-g', pendiente: 'pill-a', baja: 'pill-s' }[s.estado]">{{ s.estado }}</span>
              <span style="color:var(--tx-lo);font-size:12.5px">alta {{ s.alta || '—' }}</span>
              <span style="color:var(--tx-lo);font-size:12.5px">último envío {{ s.ultimo_envio || '—' }}</span>
              <!-- Reenviar sólo tiene sentido en las pendientes: al confirmado
                   no hay nada que mandarle y al que se dio de baja no se le
                   escribe más. -->
              <button v-if="s.estado === 'pendiente'" class="btn btn-g btn-sm" style="margin-left:auto"
                      :disabled="trabajandoSuscriptor"
                      @click="reenviarConfirmacion(s.email)">
                {{ reenviando === s.email ? 'Un momento…' : 'Reenviar confirmación' }}
              </button>
              <!-- Dos clicks a propósito: eliminar borra de verdad. -->
              <button class="btn btn-g btn-sm" :style="s.estado === 'pendiente' ? '' : 'margin-left:auto'"
                      :disabled="eliminandoSuscriptor === s.email && trabajandoSuscriptor"
                      @click="eliminarSuscriptor(s.email)">
                {{ eliminandoSuscriptor === s.email
                  ? (trabajandoSuscriptor ? 'Un momento…' : '¿Seguro? Sí, eliminar')
                  : 'Eliminar' }}
              </button>
            </div>
            <p v-if="avisoSuscriptores" style="margin:10px 0 0;font-size:13px">{{ avisoSuscriptores }}</p>
          </div>
        </div>

        <!-- Visitas de Radar: colapsable como el del institucional (12/8).
             Los totales se ven igual sin abrirlo. -->
        <div class="card card-p mb-16">
          <div style="display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;cursor:pointer"
               @click="vistasAbierto = !vistasAbierto">
            <span class="c-lbl">Visitas del sitio público — radar-comex</span>
            <span class="pill pill-g">{{ vistas?.total || 0 }} de personas</span>
            <span class="pill pill-a">{{ vistas?.robots || 0 }} de robots</span>
            <span v-if="vistas?.dias_contados" class="pill pill-s">
              {{ vistas.promedio }} por día · {{ vistas.dias_contados }} {{ vistas.dias_contados === 1 ? 'día contado' : 'días contados' }}
            </span>
            <span style="margin-left:auto;color:var(--tx-lo);font-size:11px">
              {{ vistasAbierto ? 'cerrar ▲' : 'abrir ▼' }}
            </span>
          </div>
          <template v-if="vistasAbierto">
          <p style="color:var(--tx-lo);font-size:12.5px;margin:10px 0 12px">
            Personas y robots contados por separado, con el origen de cada ingreso
            clasificado al vuelo — nunca se guardan IPs ni URLs, sólo la categoría.
            Las visitas internas del sistema no cuentan. Se guardan los últimos 60 días.
          </p>

          <!-- El detalle a la izquierda; la tendencia semanal fija de 60
               días al lateral derecho (en angosto, baja). -->
          <div style="display:flex;gap:22px;flex-wrap:wrap;align-items:flex-start">
          <div style="flex:1 1 460px;min-width:0">

          <!-- Filtro por calendario: un rango a elección más los atajos de
               siempre. Todo el año en una lista no se puede leer. -->
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:12px">
            <input v-model="vistasDesde" type="date" class="fi" style="max-width:150px;padding:5px 10px;font-size:12.5px">
            <span style="color:var(--tx-lo);font-size:12.5px">a</span>
            <input v-model="vistasHasta" type="date" class="fi" style="max-width:150px;padding:5px 10px;font-size:12.5px">
            <button class="btn btn-sm" :disabled="cargandoVistas" @click="cargarVistas">
              {{ cargandoVistas ? 'Un momento…' : 'Ver' }}
            </button>
            <span style="display:flex;gap:6px;margin-left:4px">
              <button v-for="n in [7, 30, 90]" :key="n" class="btn btn-g btn-sm"
                      :disabled="cargandoVistas" @click="rangoRapido(n)">{{ n }} días</button>
            </span>
          </div>
          <p v-if="avisoVistas" style="margin:0 0 10px;font-size:13px">{{ avisoVistas }}</p>

          <div v-if="!vistas?.dias?.length" class="tbl-empty">
            Sin visitas registradas en el período elegido.
          </div>
          <template v-else>
            <!-- El período entero: qué se mira y de dónde llega la gente. -->
            <div style="display:grid;grid-template-columns:110px 1fr;gap:6px 10px;align-items:baseline;margin-bottom:10px">
              <span style="font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo)">Qué se mira</span>
              <div style="display:flex;gap:6px;flex-wrap:wrap">
                <span v-for="p in vistas.paginas" :key="p.pagina" class="pill pill-s">
                  {{ p.pagina }} <b style="margin-left:4px">{{ p.total }}</b>
                </span>
              </div>
              <span style="font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo)">De dónde llegan</span>
              <div style="display:flex;gap:6px;flex-wrap:wrap">
                <span v-for="o in vistas.origenes" :key="o.origen" class="pill pill-b">
                  {{ o.origen }} <b style="margin-left:4px">{{ o.total }}</b>
                </span>
              </div>
            </div>

            <!-- Un día por fila; click abre el detalle. Se ven ~7 días y el
                 resto baja con scroll (12/8). -->
            <div style="max-height:252px;overflow-y:auto">
            <div v-for="d in vistas.dias" :key="d.fecha"
                 style="border-top:1px solid var(--bd, rgba(128,128,128,.18))">
              <div style="display:flex;gap:12px;align-items:baseline;flex-wrap:wrap;padding:9px 0;
                          font-size:13.5px;cursor:pointer"
                   @click="diaAbierto = diaAbierto === d.fecha ? '' : d.fecha">
                <span style="min-width:130px;color:var(--tx-hi)">{{ formatoFecha(d.fecha) }}</span>
                <b>{{ d.total }}</b>
                <span style="color:var(--tx-lo);font-size:12px">personas</span>
                <span v-if="d.robots" style="color:var(--tx-lo);font-size:12px">· {{ d.robots }} robots</span>
                <span style="color:var(--tx-lo);font-size:12.5px">{{ paginasEnLinea(d.paginas, 3) }}</span>
                <span style="margin-left:auto;color:var(--tx-lo);font-size:11px">
                  {{ diaAbierto === d.fecha ? 'cerrar ▲' : 'detalle ▼' }}
                </span>
              </div>
              <div v-if="diaAbierto === d.fecha"
                   style="display:grid;grid-template-columns:1fr 1fr;gap:2px 26px;
                          padding:2px 0 14px 130px;font-size:12.5px;max-width:560px">
                <div>
                  <div style="font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo);margin-bottom:4px">Páginas</div>
                  <div v-for="(cuenta, pagina) in d.paginas" :key="'p' + pagina"
                       style="display:flex;justify-content:space-between;gap:8px;color:var(--tx-md);padding:1px 0">
                    <span>{{ pagina }}</span><b style="color:var(--tx-hi)">{{ cuenta }}</b>
                  </div>
                </div>
                <div>
                  <div style="font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo);margin-bottom:4px">Origen del ingreso</div>
                  <div v-for="(cuenta, origen) in d.origenes" :key="'o' + origen"
                       style="display:flex;justify-content:space-between;gap:8px;color:var(--tx-md);padding:1px 0">
                    <span>{{ origen }}</span><b style="color:var(--tx-hi)">{{ cuenta }}</b>
                  </div>
                  <div v-if="d.robots" style="display:flex;justify-content:space-between;gap:8px;color:var(--tx-lo);padding:4px 0 0;border-top:1px dashed var(--border, #dde);margin-top:4px">
                    <span>robots (aparte)</span><b>{{ d.robots }}</b>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </template>
          </div>

          <aside style="flex:0 1 300px;min-width:250px">
            <div style="font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo);margin-bottom:6px">
              Tendencia diaria · últimos 60 días
            </div>
            <!-- Dos gráficos apilados, cada uno con SU escala: con una sola,
                 los picos de robots aplastaban la línea de personas. -->
            <div style="font-size:11px;font-weight:700;color:#2B7A4C;margin:2px 0">Personas</div>
            <svg viewBox="0 0 300 112" style="width:100%;height:auto;display:block">
              <line x1="0" y1="10" x2="300" y2="10" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
              <line x1="0" y1="58" x2="300" y2="58" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
              <line x1="0" y1="106" x2="300" y2="106" stroke="var(--border, #dde)" stroke-width="1"/>
              <text x="0" y="7" font-size="9" fill="var(--tx-lo)">{{ miles(topeCampo(tendencia, 'personas')) }}</text>
              <text x="0" y="55" font-size="9" fill="var(--tx-lo)">{{ miles(mitadCampo(tendencia, 'personas')) }}</text>
              <polyline :points="lineaDe(tendencia, 'personas')" fill="none" stroke="#2B7A4C" stroke-width="2.5"/>
              <polyline :points="tramoParcial(tendencia, 'personas')" fill="none" stroke="#2B7A4C" stroke-width="2.5" opacity=".7" stroke-dasharray="4 5"/>
            </svg>
            <div style="font-size:11px;font-weight:700;color:#C8891A;margin:10px 0 2px">Robots</div>
            <svg viewBox="0 0 300 124" style="width:100%;height:auto;display:block">
              <line x1="0" y1="10" x2="300" y2="10" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
              <line x1="0" y1="58" x2="300" y2="58" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
              <line x1="0" y1="106" x2="300" y2="106" stroke="var(--border, #dde)" stroke-width="1"/>
              <text x="0" y="7" font-size="9" fill="var(--tx-lo)">{{ miles(topeCampo(tendencia, 'robots')) }}</text>
              <text x="0" y="55" font-size="9" fill="var(--tx-lo)">{{ miles(mitadCampo(tendencia, 'robots')) }}</text>
              <polyline :points="lineaDe(tendencia, 'robots')" fill="none" stroke="#C8891A" stroke-width="2"/>
              <polyline :points="tramoParcial(tendencia, 'robots')" fill="none" stroke="#C8891A" stroke-width="2" opacity=".6" stroke-dasharray="4 5"/>
              <text x="0" y="121" font-size="9" fill="var(--tx-lo)">{{ desdeDe(tendencia) }}</text>
              <text x="300" y="121" text-anchor="end" font-size="9" fill="var(--tx-lo)">hoy</text>
            </svg>
            <div v-if="esParcial(tendencia)" style="font-size:11.5px;color:var(--tx-lo);margin-top:6px">⋯ hoy, en curso</div>
          </aside>
          </div>
          </template>
        </div>

        <!-- Visitas del institucional: el mismo contador, otro sitio. Va
             colapsado porque es consulta ocasional; los totales se ven
             igual sin abrirlo. -->
        <div class="card card-p mb-16">
          <div style="display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;cursor:pointer"
               @click="instAbierto = !instAbierto">
            <span class="c-lbl">Visitas del sitio institucional — mhlm.ar</span>
            <span class="pill pill-g">{{ instVistas?.total || 0 }} de personas</span>
            <span class="pill pill-a">{{ instVistas?.robots || 0 }} de robots</span>
            <span v-if="instVistas?.dias_contados" class="pill pill-s">
              {{ instVistas.promedio }} por día · {{ instVistas.dias_contados }} {{ instVistas.dias_contados === 1 ? 'día contado' : 'días contados' }}
            </span>
            <span style="margin-left:auto;color:var(--tx-lo);font-size:11px">
              {{ instAbierto ? 'cerrar ▲' : 'abrir ▼' }}
            </span>
          </div>

          <template v-if="instAbierto">
            <p style="color:var(--tx-lo);font-size:12.5px;margin:10px 0 12px">
              El contador de mhlm.ar, separado del de Radar: mismas reglas —personas y
              robots aparte, sólo categorías, nunca IPs ni URLs. Quien cruza desde
              Radar COMEX aparece con su propio origen.
            </p>

            <div style="display:flex;gap:22px;flex-wrap:wrap;align-items:flex-start">
            <div style="flex:1 1 460px;min-width:0">

            <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:12px">
              <input v-model="instDesde" type="date" class="fi" style="max-width:150px;padding:5px 10px;font-size:12.5px">
              <span style="color:var(--tx-lo);font-size:12.5px">a</span>
              <input v-model="instHasta" type="date" class="fi" style="max-width:150px;padding:5px 10px;font-size:12.5px">
              <button class="btn btn-sm" :disabled="cargandoInst" @click="cargarVistasInst">
                {{ cargandoInst ? 'Un momento…' : 'Ver' }}
              </button>
              <span style="display:flex;gap:6px;margin-left:4px">
                <button v-for="n in [7, 30, 90]" :key="n" class="btn btn-g btn-sm"
                        :disabled="cargandoInst" @click="rangoRapidoInst(n)">{{ n }} días</button>
              </span>
            </div>
            <p v-if="avisoInst" style="margin:0 0 10px;font-size:13px">{{ avisoInst }}</p>

            <div v-if="!instVistas?.dias?.length" class="tbl-empty">
              Sin visitas registradas en el período elegido.
            </div>
            <template v-else>
              <div style="display:grid;grid-template-columns:110px 1fr;gap:6px 10px;align-items:baseline;margin-bottom:10px">
                <span style="font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo)">Qué se mira</span>
                <div style="display:flex;gap:6px;flex-wrap:wrap">
                  <span v-for="p in instVistas.paginas" :key="p.pagina" class="pill pill-s">
                    {{ p.pagina }} <b style="margin-left:4px">{{ p.total }}</b>
                  </span>
                </div>
                <span style="font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo)">De dónde llegan</span>
                <div style="display:flex;gap:6px;flex-wrap:wrap">
                  <span v-for="o in instVistas.origenes" :key="o.origen" class="pill pill-b">
                    {{ o.origen }} <b style="margin-left:4px">{{ o.total }}</b>
                  </span>
                </div>
              </div>

              <div style="max-height:252px;overflow-y:auto">
              <div v-for="d in instVistas.dias" :key="'i' + d.fecha"
                   style="border-top:1px solid var(--bd, rgba(128,128,128,.18))">
                <div style="display:flex;gap:12px;align-items:baseline;flex-wrap:wrap;padding:9px 0;
                            font-size:13.5px;cursor:pointer"
                     @click="instDiaAbierto = instDiaAbierto === d.fecha ? '' : d.fecha">
                  <span style="min-width:130px;color:var(--tx-hi)">{{ formatoFecha(d.fecha) }}</span>
                  <b>{{ d.total }}</b>
                  <span style="color:var(--tx-lo);font-size:12px">personas</span>
                  <span v-if="d.robots" style="color:var(--tx-lo);font-size:12px">· {{ d.robots }} robots</span>
                  <span style="color:var(--tx-lo);font-size:12.5px">{{ paginasEnLinea(d.paginas, 3) }}</span>
                  <span style="margin-left:auto;color:var(--tx-lo);font-size:11px">
                    {{ instDiaAbierto === d.fecha ? 'cerrar ▲' : 'detalle ▼' }}
                  </span>
                </div>
                <div v-if="instDiaAbierto === d.fecha"
                     style="display:grid;grid-template-columns:1fr 1fr;gap:2px 26px;
                            padding:2px 0 14px 130px;font-size:12.5px;max-width:560px">
                  <div>
                    <div style="font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo);margin-bottom:4px">Páginas</div>
                    <div v-for="(cuenta, pagina) in d.paginas" :key="'ip' + pagina"
                         style="display:flex;justify-content:space-between;gap:8px;color:var(--tx-md);padding:1px 0">
                      <span>{{ pagina }}</span><b style="color:var(--tx-hi)">{{ cuenta }}</b>
                    </div>
                  </div>
                  <div>
                    <div style="font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo);margin-bottom:4px">Origen del ingreso</div>
                    <div v-for="(cuenta, origen) in d.origenes" :key="'io' + origen"
                         style="display:flex;justify-content:space-between;gap:8px;color:var(--tx-md);padding:1px 0">
                      <span>{{ origen }}</span><b style="color:var(--tx-hi)">{{ cuenta }}</b>
                    </div>
                    <div v-if="d.robots" style="display:flex;justify-content:space-between;gap:8px;color:var(--tx-lo);padding:4px 0 0;border-top:1px dashed var(--border, #dde);margin-top:4px">
                      <span>robots (aparte)</span><b>{{ d.robots }}</b>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </template>
            </div>

            <aside style="flex:0 1 300px;min-width:250px">
              <div style="font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--tx-lo);margin-bottom:6px">
                Tendencia diaria · últimos 60 días
              </div>
              <div style="font-size:11px;font-weight:700;color:#2B7A4C;margin:2px 0">Personas</div>
              <svg viewBox="0 0 300 112" style="width:100%;height:auto;display:block">
                <line x1="0" y1="10" x2="300" y2="10" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
                <line x1="0" y1="58" x2="300" y2="58" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
                <line x1="0" y1="106" x2="300" y2="106" stroke="var(--border, #dde)" stroke-width="1"/>
                <text x="0" y="7" font-size="9" fill="var(--tx-lo)">{{ miles(topeCampo(tendenciaInst, 'personas')) }}</text>
                <text x="0" y="55" font-size="9" fill="var(--tx-lo)">{{ miles(mitadCampo(tendenciaInst, 'personas')) }}</text>
                <polyline :points="lineaDe(tendenciaInst, 'personas')" fill="none" stroke="#2B7A4C" stroke-width="2.5"/>
                <polyline :points="tramoParcial(tendenciaInst, 'personas')" fill="none" stroke="#2B7A4C" stroke-width="2.5" opacity=".7" stroke-dasharray="4 5"/>
              </svg>
              <div style="font-size:11px;font-weight:700;color:#C8891A;margin:10px 0 2px">Robots</div>
              <svg viewBox="0 0 300 124" style="width:100%;height:auto;display:block">
                <line x1="0" y1="10" x2="300" y2="10" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
                <line x1="0" y1="58" x2="300" y2="58" stroke="var(--border, #dde)" stroke-width="1" stroke-dasharray="2 4"/>
                <line x1="0" y1="106" x2="300" y2="106" stroke="var(--border, #dde)" stroke-width="1"/>
                <text x="0" y="7" font-size="9" fill="var(--tx-lo)">{{ miles(topeCampo(tendenciaInst, 'robots')) }}</text>
                <text x="0" y="55" font-size="9" fill="var(--tx-lo)">{{ miles(mitadCampo(tendenciaInst, 'robots')) }}</text>
                <polyline :points="lineaDe(tendenciaInst, 'robots')" fill="none" stroke="#C8891A" stroke-width="2"/>
                <polyline :points="tramoParcial(tendenciaInst, 'robots')" fill="none" stroke="#C8891A" stroke-width="2" opacity=".6" stroke-dasharray="4 5"/>
                <text x="0" y="121" font-size="9" fill="var(--tx-lo)">{{ desdeDe(tendenciaInst) }}</text>
                <text x="300" y="121" text-anchor="end" font-size="9" fill="var(--tx-lo)">hoy</text>
              </svg>
              <div v-if="esParcial(tendenciaInst)" style="font-size:11.5px;color:var(--tx-lo);margin-top:6px">⋯ hoy, en curso</div>
            </aside>
            </div>
          </template>
        </div>

        <div class="g-2">
          <!-- Horarios -->
          <div class="card card-p">
            <div class="c-lbl" style="margin-bottom:6px">Horarios del día editorial</div>
            <p style="color:var(--tx-lo);font-size:12.5px;margin:0 0 14px">
              Hora argentina, de lunes a viernes. Un cambio rige desde la próxima corrida.
            </p>

            <div v-for="(rotulo, cual) in rotulosHorario" :key="cual"
                 style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:10px">
              <span style="min-width:230px;font-size:13.5px">{{ rotulo }}</span>
              <input v-model="horarios[cual]" type="time" class="fi" style="max-width:120px;padding:5px 10px;font-size:13px">
            </div>

            <!-- ⚠️ `!== ''` y no el valor a secas: en Vue 3, :disabled con
                 cadena vacía RENDERIZA disabled="" (botón muerto). En Vue 2
                 la cadena vacía era falsa; acá no. Costó encontrarlo. -->
            <button class="btn" :disabled="guardandoConfig !== ''" style="margin-top:6px" @click="guardarConfig('horarios')">
              {{ guardandoConfig === 'horarios' ? 'Un momento…' : 'Guardar horarios' }}
            </button>
            <p v-if="avisoHorarios" style="margin:10px 0 0;font-size:13px">{{ avisoHorarios }}</p>
          </div>

          <!-- Correo remitente -->
          <div class="card card-p">
            <div class="c-lbl" style="margin-bottom:6px">Correo remitente</div>
            <p style="color:var(--tx-lo);font-size:12.5px;margin:0 0 14px">
              El nombre y la dirección que ven los suscriptores. La dirección tiene que existir
              en el dominio del correo de MHLM: una inventada termina en spam.
            </p>

            <div style="display:flex;flex-direction:column;gap:10px;max-width:360px">
              <input v-model="remitente.nombre" class="fi" placeholder="Nombre (Radar COMEX)"
                     style="padding:7px 12px;font-size:13px">
              <input v-model="remitente.direccion" type="email" class="fi" placeholder="direccion@mhlm.com.ar"
                     style="padding:7px 12px;font-size:13px">
            </div>

            <button class="btn" :disabled="guardandoConfig !== ''" style="margin-top:14px" @click="guardarConfig('remitente')">
              {{ guardandoConfig === 'remitente' ? 'Un momento…' : 'Guardar remitente' }}
            </button>
            <p v-if="avisoRemitente" style="margin:10px 0 0;font-size:13px">{{ avisoRemitente }}</p>
          </div>
        </div>
      </template>
    </div>
    </template>
  </section>
</template>

<script>
/*
 * El módulo Radar COMEX.
 *
 * Radar COMEX es un proyecto aparte: lee el Boletín Oficial todas las mañanas,
 * detecta lo que impacta en comercio exterior y redacta las piezas. Esta
 * pantalla es el único paso que necesita una persona: mirar lo que escribió y
 * aprobarlo o descartarlo.
 *
 * No habla con Radar directo. Va contra /api/radar de esta misma app, que
 * guarda el token del lado del servidor y le dice a Radar quién está revisando.
 */
import api from '../services/api';

export default {
  name: 'RadarComexView',
  data() {
    return {
      dias: [], fecha: '', metricas: {},
      // Lo publicable del día y cuál está abierta a la derecha. `rotas`
      // guarda las placas que no cargaron, por clave de pieza y posición.
      publicables: [], elegida: '', rotas: {},
      // `creando` guarda el título mientras se arma: es lo que muestra el
      // aviso de pantalla completa, y de paso bloquea tocar dos temas.
      biblioteca: null, filtroTema: '', creando: '', repite: null,
      // Lo ya publicado arranca escondido en la biblioteca.
      verPublicadas: false,
      ficha: null, urlRadar: '', copiado: '',
      marcando: false,
      cargando: true, trabajando: false,
      aviso: '', fatal: '',
      // La solapa Configuración: se carga recién cuando se abre.
      solapa: 'publicaciones', config: null, cargandoConfig: false,
      guardandoConfig: '', avisoHorarios: '', avisoRemitente: '',
      eliminandoSuscriptor: '', trabajandoSuscriptor: false, avisoSuscriptores: '',
      reenviando: '',
      // Visitas: el rango del calendario y el día abierto en detalle.
      // Los dos bloques arrancan colapsados; los totales se ven igual.
      vistasAbierto: false,
      vistas: null, vistasDesde: '', vistasHasta: '',
      cargandoVistas: false, avisoVistas: '', diaAbierto: '',
      // Visitas del institucional (mhlm.ar), en su bloque colapsable.
      instAbierto: false, instVistas: null, instDesde: '', instHasta: '',
      cargandoInst: false, avisoInst: '', instDiaAbierto: '',
      // Tendencia semanal fija (60 días) de cada contador, para el lateral.
      tendencia: null, tendenciaInst: null,
      horarios: { pipeline: '', recordatorio: '', boletin: '' },
      remitente: { nombre: '', direccion: '' },
      rotulosHorario: {
        pipeline: 'Lectura del Boletín y redacción',
        recordatorio: 'Recordatorio de publicar (a tu correo)',
        boletin: 'Boletín a los suscriptores',
      },
    };
  },
  async mounted() {
    // La dirección de Radar sale del backend y no de una variable del build:
    // así, si cambia, no hay que volver a compilar el frontend.
    try {
      const { data } = await api.get('/radar/estado');
      this.urlRadar = data.url || '';
      if (!data.token_configurado) {
        this.fatal = 'Falta configurar el acceso a Radar COMEX en el servidor (RADAR_API_TOKEN).';
        this.cargando = false;
        return;
      }
    } catch (e) {
      this.fatal = this.mensajeDe(e, 'No se pudo consultar el estado de Radar COMEX.');
      this.cargando = false;
      return;
    }

    try {
      const { data } = await api.get('/radar/dias');
      this.dias = data.dias || [];
      this.fecha = this.dias[0]?.fecha || '';
    } catch (e) {
      if (!e?.isAuthExpired) this.fatal = this.mensajeDe(e, 'Radar COMEX no respondió.');
      this.cargando = false;
      return;
    }

    await this.cargarDia();
  },
  computed: {
    /* El texto de la pieza, se llame como se llame según el canal. */
    textoDeLaPieza() {
      const c = this.ficha?.contenido || {};
      return c.texto || c.linkedin || c.caption || '';
    },

    /* Los hashtags sueltos, para pegarlos aparte si hace falta. */
    hashtags() {
      return (this.ficha?.hashtags || this.ficha?.contenido?.hashtags || []).join(' ');
    },

    /*
     * Lo que se pega de una. El pie —hashtags, fuente y el sitio— lo compone
     * el servidor, así que la pantalla, el ZIP y el script dicen exactamente
     * lo mismo. Antes cada uno lo armaba por su cuenta y el enlace al sitio no
     * estaba en ninguno.
     */
    textoParaPublicar() {
      const pie = this.ficha?.pie
        || [this.hashtags, this.ficha?.fuente].filter(Boolean).join('\n\n');

      return [this.textoDeLaPieza, pie].filter(Boolean).join('\n\n');
    },

    /* La publicación abierta a la derecha, la que está marcada en la lista. */
    publicacion() {
      return this.publicables.find((p) => p.clave === this.elegida) || null;
    },

    /*
     * Lo que falta publicar. La lista del día muestra sólo esto: lo que ya
     * salió no es trabajo pendiente y ocupaba el mismo lugar que lo que sí lo
     * es. Queda arriba, chiquito, como referencia.
     */
    pendientes() {
      return this.publicables.filter((p) => !p.publicada);
    },

    /* Lo que ya salió al aire ese día. */
    publicadasHoy() {
      return this.publicables.filter((p) => p.publicada);
    },

    /*
     * La biblioteca sin lo ya publicado: la lista es para elegir qué sacar,
     * no un archivo de lo que salió. Se puede volver a mostrar.
     */
    temasVisibles() {
      const temas = this.biblioteca?.temas || [];

      return this.verPublicadas ? temas : temas.filter((t) => !t.publicada_el);
    },

    /* Cuántos quedaron escondidos por ya haber salido al aire. */
    temasPublicados() {
      return (this.biblioteca?.temas || []).filter((t) => t.publicada_el).length;
    },

    /* Lo visible, además filtrado por lo que se escriba en el buscador. */
    temasFiltrados() {
      const t = this.filtroTema.trim().toLowerCase();

      if (!t) return this.temasVisibles;

      return this.temasVisibles
        .filter((x) => (`${x.titulo} ${this.serieLegible(x.serie)}`).toLowerCase().includes(t));
    },

  },
  methods: {
    async cargarDia() {
      this.cargando = true;
      this.aviso = '';
      try {
        const { data } = await api.get(`/radar/dia${this.fecha ? '/' + this.fecha : ''}`);
        this.metricas = data.metricas || {};
        this.publicables = data.publicables || [];
        this.biblioteca = data.biblioteca || null;

        /*
         * La selección sobrevive al refresco si la pieza sigue estando —así
         * marcar «ya publiqué» no salta a otra publicación—; si no, se abre
         * la primera, que es la que falta publicar.
         */
        if (!this.publicables.some((p) => p.clave === this.elegida)) {
          this.elegir(this.publicables[0]?.clave || '');
        } else if (this.ficha?.id !== this.publicacion?.ig) {
          this.elegir(this.elegida);
        }
      } catch (e) {
        if (!e?.isAuthExpired) this.aviso = this.mensajeDe(e, 'No se pudo traer el día.');
      } finally {
        this.cargando = false;
      }
    },

    /*
     * Elegir una publicación de la lista: además de marcarla, trae su texto
     * y sus botones ahí mismo. No hay pantalla aparte que abrir.
     */
    elegir(clave) {
      this.elegida = clave;
      this.ficha = null;

      const p = this.publicables.find((x) => x.clave === clave);
      if (p) this.abrirFicha(p.ig);
    },

    /*
     * Armar la publicación del día con un tema de la biblioteca.
     *
     * Tarda: crea los dos borradores y renderiza las placas ahí mismo. Por eso
     * el aviso de pantalla completa mientras trabaja, y por eso queda abierta
     * la pieza recién armada cuando termina.
     */
    async armarTema(tema, forzar = false) {
      if (this.creando) return;

      /*
       * Si ese tema YA está armado en este día, no se rearma: se abre.
       * Rearmarlo descartaba la pieza que estaba y creaba otra igual, y el
       * guardia de repetición saltaba contra la pieza que uno mismo acababa
       * de hacer.
       */
      const yaEsta = this.publicables.find((p) => p.tema_id === tema.id);
      if (yaEsta && !forzar) {
        this.elegir(yaEsta.clave);
        this.aviso = `«${tema.titulo}» ya está armada para este día.`;
        return;
      }

      this.creando = tema.titulo;
      this.aviso = '';
      this.repite = null;
      try {
        const { data } = await api.post(`/radar/dia/${this.fecha}/biblioteca`, {
          tema_id: tema.id,
          ...(forzar ? { forzar: true } : {}),
        });
        // El backend manda lo recién armado primero, así que soltando la
        // selección anterior se abre solo.
        this.elegida = '';
        await this.cargarDia();
        this.aviso = data.listas_para_revisar
          ? `Listo: «${data.tema}» quedó armada y con placas.`
          : `«${data.tema}» quedó armada, pero las placas no se generaron: salen en la próxima corrida.`;
      } catch (e) {
        if (e?.isAuthExpired) return;

        /*
         * Un tema que salió hace poco no se arma de una: se avisa y se
         * pregunta. Repetir puede ser lo correcto —salió una norma que lo hace
         * pertinente— pero tiene que ser una decisión, no un descuido.
         */
        if (e?.response?.data?.repite) {
          this.repite = { tema, motivo: this.mensajeDe(e, 'Ese tema ya salió hace poco.') };
          return;
        }

        this.aviso = this.mensajeDe(e, 'No se pudo armar la publicación.');
      } finally {
        this.creando = '';
      }
    },

    /*
     * El puntaje de interés, dicho en palabras.
     *
     * Los 60 temas puntúan entre 19 y 24 sobre 25, así que un número solo no
     * distingue nada: leído contra esa escala, 24 es lo más fuerte que hay y
     * 19 es material de relleno. Los cortes salen de esa distribución real.
     */
    nivelInteres(n) {
      if (n >= 23) return { grado: 4, texto: 'muy alto' };
      if (n >= 21) return { grado: 3, texto: 'alto' };
      if (n >= 19) return { grado: 2, texto: 'medio' };
      return { grado: 1, texto: 'bajo' };
    },

    /* Las series viajan como identificador; acá se muestran en criollo. */
    serieLegible(serie) {
      return ({
        hoy_en_el_boletin: 'Hoy en el Boletín',
        aduana_sin_vueltas: 'Aduana sin vueltas',
        tramite_en_cinco_pasos: 'Un trámite en cinco pasos',
        sabias_que: '¿Sabías que…?',
        mito_o_realidad: 'Mito o realidad',
        antes_y_despues: 'Antes y después',
        error_que_detiene: 'El error que detiene una operación',
        curiosidades_comex: 'Curiosidades COMEX',
      })[serie] || serie;
    },

    /* Copiar al portapapeles, con el aviso en el propio botón. */
    async copiar(texto, cual) {
      try {
        await navigator.clipboard.writeText(texto);
      } catch {
        // Sin permiso de portapapeles: se cae al método viejo, que siempre anda.
        const caja = document.createElement('textarea');
        caja.value = texto;
        document.body.appendChild(caja);
        caja.select();
        document.execCommand('copy');
        caja.remove();
      }
      this.copiado = cual;
      setTimeout(() => { if (this.copiado === cual) this.copiado = ''; }, 2000);
    },

    async abrirFicha(id) {
      this.aviso = '';
      try {
        const { data } = await api.get(`/radar/borrador/${id}`);
        this.ficha = data;
        // Sin subir la pantalla: la pieza se abre al costado de la lista, no
        // en otra pantalla, así que saltar arriba sólo desorientaba.
      } catch (e) {
        if (!e?.isAuthExpired) this.aviso = this.mensajeDe(e, 'No se pudo abrir el borrador.');
      }
    },

    cerrarFicha() { this.ficha = null; this.aviso = ''; },

    /*
     * «Ya la publiqué»: aprueba si hace falta (mismo gate) y deja PUBLICADO
     * con auditoría. Con ambas=true marca también la pieza del otro canal.
     */
    async marcarPublicada(ambas) {
      this.marcando = true;
      this.aviso = '';
      try {
        const { data } = await api.post(`/radar/borrador/${this.ficha.id}/publicada`, {
          tambien_hermano: !!ambas,
        });
        const piezas = data.piezas || [];
        const ok = piezas.filter((p) => p.publicada);
        const mal = piezas.filter((p) => !p.publicada);
        this.aviso = ok.length
          ? `✓ Publicada${ok.length > 1 ? 's' : ''}: ${ok.map((p) => p.canal).join(' y ')}.`
            + (mal.length ? ` No se pudo: ${mal.map((p) => `${p.canal} (${p.error})`).join('; ')}` : '')
          : `No se pudo marcar: ${mal.map((p) => p.error).join('; ')}`;
        await this.cargarDia();
        // La ficha se refresca para que la pastilla ✓ publicada aparezca ya.
        const { data: fichaNueva } = await api.get(`/radar/borrador/${this.ficha.id}`);
        this.ficha = fichaNueva;
      } catch (e) {
        if (!e?.isAuthExpired) this.aviso = this.mensajeDe(e, 'No se pudo marcar como publicada.');
      } finally {
        this.marcando = false;
      }
    },

    async descargar() {
      this.trabajando = true;
      try {
        const r = await api.get(`/radar/borrador/${this.ficha.id}/paquete`, { responseType: 'blob' });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `radar-comex-borrador-${this.ficha.id}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        if (!e?.isAuthExpired) this.aviso = 'No se pudo bajar el paquete.';
      } finally {
        this.trabajando = false;
      }
    },

    /*
     * La `v` del asset va en la dirección a propósito. Las placas se sirven
     * con caché de un día: al regenerarlas, el navegador seguía mostrando la
     * vieja y parecía que el cambio no se había subido. Con la marca de
     * tiempo, una placa nueva es una dirección nueva.
     */
    urlPlaca(id, asset) {
      const orden = typeof asset === 'object' ? asset.orden : asset;
      const v = typeof asset === 'object' && asset.v ? `?v=${asset.v}` : '';
      return `${this.urlRadar}/placa/${id}/${orden}.png${v}`;
    },

    /* Las evidencias pueden venir como texto o como objeto con la cita adentro. */
    textoEvidencia(ev) {
      if (typeof ev === 'string') return ev;
      return ev?.cita || ev?.texto || JSON.stringify(ev);
    },

    formatoFecha(f) {
      if (!f) return '';
      // Se parte la fecha a mano: `new Date('2026-07-31')` la interpreta en UTC
      // y en Argentina muestra el día anterior.
      const [a, m, d] = f.split('-').map(Number);
      return new Date(a, m - 1, d).toLocaleDateString('es-AR', {
        weekday: 'long', day: 'numeric', month: 'long',
      });
    },
    mensajeDe(e, porDefecto) {
      return e?.response?.data?.message || e?.response?.data?.error || porDefecto;
    },

    /* ── La solapa Configuración ────────────────────────────────────── */

    estiloSolapa(cual) {
      const activa = this.solapa === cual;
      return {
        background: 'none', border: 'none', cursor: 'pointer', font: 'inherit',
        fontSize: '13.5px', padding: '10px 2px', marginBottom: '-1px',
        color: activa ? 'var(--tx-hi)' : 'var(--tx-md)',
        fontWeight: activa ? '600' : '400',
        borderBottom: activa ? '2px solid var(--tx-hi)' : '2px solid transparent',
      };
    },

    async abrirConfiguracion() {
      this.solapa = 'configuracion';
      if (!this.config) await this.cargarConfiguracion();
    },

    async cargarConfiguracion() {
      this.cargandoConfig = true;
      try {
        const { data } = await api.get('/radar/configuracion');
        this.config = data;
        this.horarios = { ...this.horarios, ...(data.ajustes?.horarios || {}) };
        this.remitente = { ...this.remitente, ...(data.ajustes?.remitente || {}) };
        this.vistas = data.vistas || null;
        this.vistasDesde = data.vistas?.desde || '';
        this.vistasHasta = data.vistas?.hasta || '';
        // El institucional llega en la misma carga: su bloque arranca con
        // los últimos 14 días sin pedir nada más.
        this.instVistas = data.vistas_institucional || null;
        this.instDesde = data.vistas_institucional?.desde || '';
        this.instHasta = data.vistas_institucional?.hasta || '';
        this.tendencia = data.tendencia || null;
        this.tendenciaInst = data.tendencia_institucional || null;
      } catch (e) {
        if (!e?.isAuthExpired) this.avisoHorarios = this.mensajeDe(e, 'No se pudo traer la configuración.');
      } finally {
        this.cargandoConfig = false;
      }
    },

    /* Las visitas del rango elegido en el calendario. */
    async cargarVistas() {
      if (!this.vistasDesde || !this.vistasHasta) return;

      this.cargandoVistas = true;
      this.avisoVistas = '';
      this.diaAbierto = '';
      try {
        const { data } = await api.get('/radar/configuracion/vistas', {
          params: { desde: this.vistasDesde, hasta: this.vistasHasta },
        });
        this.vistas = data.vistas || null;
      } catch (e) {
        if (!e?.isAuthExpired) this.avisoVistas = this.mensajeDe(e, 'No se pudo traer el período.');
      } finally {
        this.cargandoVistas = false;
      }
    },

    /*
     * Los puntos del gráfico de tendencia: una línea por serie, escala
     * compartida (la real: si los robots aplastan a las personas, esa es
     * la verdad y se ve). La semana EN CURSO va aparte, punteada: con dos
     * días contados dibujaba un desplome que no existía (18/8: «¿tanta es
     * la caída? es raro el gráfico»).
     */
    puntosDe(serie, campo, desde, hasta) {
      const w = 300, h = 110;
      const tope = this.topeCampo(serie, campo);
      const paso = serie.length > 1 ? w / (serie.length - 1) : 0;
      const pts = [];
      for (let i = desde; i <= hasta; i++) {
        pts.push(`${(i * paso).toFixed(1)},${(h - 4 - ((serie[i][campo] || 0) / tope) * (h - 14)).toFixed(1)}`);
      }
      return pts.join(' ');
    },

    /* La escala de CADA serie: un tope redondo (1-2-2,5-5-10 × potencia)
       para que las referencias digan números legibles. Personas y robots
       van en gráficos separados, cada uno con la suya. */
    topeCampo(serie, campo) {
      const m = Math.max(1, ...((serie || []).map((p) => p[campo] || 0)));
      const pot = Math.pow(10, Math.floor(Math.log10(m)));
      for (const k of [1, 2, 2.5, 5, 10]) {
        if (m <= k * pot) return Math.ceil(k * pot);
      }
      return m;
    },

    mitadCampo(serie, campo) {
      return Math.round(this.topeCampo(serie, campo) / 2);
    },

    miles(n) {
      return (n || 0).toLocaleString('es-AR');
    },

    desdeDe(serie) {
      const f = serie?.[0]?.fecha || '';
      return f ? `${parseInt(f.slice(8, 10), 10)}/${parseInt(f.slice(5, 7), 10)}` : '';
    },

    lineaDe(serie, campo) {
      if (!serie?.length) return '';
      const fin = this.esParcial(serie) ? serie.length - 2 : serie.length - 1;
      return this.puntosDe(serie, campo, 0, Math.max(fin, 0));
    },

    tramoParcial(serie, campo) {
      if (!this.esParcial(serie) || serie.length < 2) return '';
      return this.puntosDe(serie, campo, serie.length - 2, serie.length - 1);
    },

    esParcial(serie) {
      return !!(serie?.length && serie[serie.length - 1].parcial);
    },

    /* Las visitas del institucional (mhlm.ar), con su propio calendario. */
    async cargarVistasInst() {
      if (!this.instDesde || !this.instHasta) return;

      this.cargandoInst = true;
      this.avisoInst = '';
      this.instDiaAbierto = '';
      try {
        const { data } = await api.get('/radar/configuracion/vistas', {
          params: { desde: this.instDesde, hasta: this.instHasta, sitio: 'institucional' },
        });
        this.instVistas = data.vistas || null;
      } catch (e) {
        if (!e?.isAuthExpired) this.avisoInst = this.mensajeDe(e, 'No se pudo traer el período.');
      } finally {
        this.cargandoInst = false;
      }
    },

    rangoRapidoInst(dias) {
      const f = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const hoy = new Date();
      const desde = new Date();
      desde.setDate(hoy.getDate() - (dias - 1));
      this.instDesde = f(desde);
      this.instHasta = f(hoy);
      this.cargarVistasInst();
    },

    /* Atajos del filtro: los últimos N días, contando hoy. */
    rangoRapido(dias) {
      const f = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const hoy = new Date();
      const desde = new Date();
      desde.setDate(hoy.getDate() - (dias - 1));
      this.vistasDesde = f(desde);
      this.vistasHasta = f(hoy);
      this.cargarVistas();
    },

    /*
     * Guarda una parte por vez —horarios o remitente— para que el aviso
     * aparezca al lado del botón que se tocó. La validación fina la hace
     * Radar; acá sólo se muestra su respuesta.
     */
    async guardarConfig(parte) {
      this.guardandoConfig = parte;
      this.avisoHorarios = '';
      this.avisoRemitente = '';

      const carga = parte === 'horarios'
        ? { horarios: this.horarios }
        : { remitente: this.remitente };

      try {
        const { data } = await api.post('/radar/configuracion/ajustes', carga);
        this.horarios = { ...this.horarios, ...(data.ajustes?.horarios || {}) };
        this.remitente = { ...this.remitente, ...(data.ajustes?.remitente || {}) };
        const listo = parte === 'horarios'
          ? 'Guardado. Rige desde la próxima corrida.'
          : 'Guardado. Los próximos correos salen con este remitente.';
        if (parte === 'horarios') this.avisoHorarios = listo; else this.avisoRemitente = listo;
      } catch (e) {
        const mensaje = this.mensajeDe(e, 'No se pudo guardar.');
        if (parte === 'horarios') this.avisoHorarios = mensaje; else this.avisoRemitente = mensaje;
      } finally {
        this.guardandoConfig = '';
      }
    },

    /*
     * Eliminar pide dos clicks: el primero arma la pregunta en el propio
     * botón, el segundo borra. Cambiar de fila desarma la pregunta.
     */
    async eliminarSuscriptor(email) {
      if (this.eliminandoSuscriptor !== email) {
        this.eliminandoSuscriptor = email;
        this.avisoSuscriptores = '';
        return;
      }

      this.trabajandoSuscriptor = true;
      try {
        const { data } = await api.post('/radar/configuracion/suscriptores/eliminar', { email });
        if (data.suscriptores) this.config = { ...this.config, suscriptores: data.suscriptores };
        this.avisoSuscriptores = `Eliminada: ${email}.`;
      } catch (e) {
        if (!e?.isAuthExpired) this.avisoSuscriptores = this.mensajeDe(e, 'No se pudo eliminar.');
      } finally {
        this.trabajandoSuscriptor = false;
        this.eliminandoSuscriptor = '';
      }
    },

    /* Reenviar la confirmación a una pendiente. Radar aplica las reglas. */
    async reenviarConfirmacion(email) {
      this.reenviando = email;
      this.trabajandoSuscriptor = true;
      this.avisoSuscriptores = '';
      this.eliminandoSuscriptor = '';
      try {
        const { data } = await api.post('/radar/configuracion/suscriptores/reenviar', { email });
        this.avisoSuscriptores = data.message || `Reenviada a ${email}.`;
      } catch (e) {
        if (!e?.isAuthExpired) this.avisoSuscriptores = this.mensajeDe(e, 'No se pudo reenviar.');
      } finally {
        this.trabajandoSuscriptor = false;
        this.reenviando = '';
      }
    },

    /* «portada 12 · norma 5» — el desglose de un día en una línea.
       Con tope: la fila muestra lo principal, el detalle completo se abre. */
    paginasEnLinea(paginas, tope = 0) {
      const todas = Object.entries(paginas || {});
      const visibles = tope > 0 ? todas.slice(0, tope) : todas;
      const resto = todas.length - visibles.length;

      return visibles.map(([pagina, cuenta]) => `${pagina} ${cuenta}`).join(' · ')
        + (resto > 0 ? ` · +${resto} más` : '');
    },
  },
};
</script>
<!-- canario de despliegue 2026-08-08: si este comentario llega a producción,
     el circuito GitHub → App Platform sigue vivo para esta app. -->

<style scoped>
/*
 * La pantalla de publicaciones: lista a la izquierda, la publicación a la
 * derecha. Ancho fijo para la lista —los títulos son largos y si la columna
 * se estira, la placa se achica— y la placa manda en el resto.
 */
.pub-split{ display:grid; grid-template-columns:340px 1fr; gap:16px; align-items:start; }
@media (max-width: 1000px){ .pub-split{ grid-template-columns:1fr; } }

/* La barra del día: una línea discreta, no un encabezado. Lo que reemplazó
   —cuatro tarjetas de métricas— ocupaba media pantalla sin cambiar nada. */
.dia-barra{
  display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:16px;
}
.dia-lbl{
  font-size:11px; letter-spacing:.08em; text-transform:uppercase; color:var(--tx-lo);
}
/* Lo ya publicado: referencial. Se puede tocar para verlo, pero no compite
   con lo que falta hacer. */
.dia-ya{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
.dia-ya-it{
  border:1px solid var(--border); background:transparent; cursor:pointer;
  font:inherit; font-size:11.5px; color:var(--tx-md);
  padding:3px 10px; border-radius:999px; max-width:340px;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.dia-ya-it:hover{ background:var(--acc-xlt); color:var(--tx-hi); }
.dia-ya-it.is-sel{ background:var(--acc-lt); color:var(--accent); border-color:var(--accent); }

.pub-col{ min-width:0; }

.pub-items{
  border:1px solid var(--border); border-radius:6px; overflow:auto;
  max-height:52vh;
}
/* La biblioteca entera: más alta, porque son sesenta temas y la idea es
   poder recorrerlos, no espiarlos de a uno. */
.pub-items-alta{ max-height:60vh; }
/* El interés, con su clasificación. Cuatro escalones de la misma familia:
   el color dice el nivel de un vistazo y el texto lo confirma. */
.pub-estrella{
  display:inline-block; padding:1px 7px; border-radius:999px;
  font-size:11px; font-weight:700; white-space:nowrap;
}
.pub-estrella.int-4{ background:var(--accent); color:#fff; }
.pub-estrella.int-3{ background:var(--acc-lt); color:var(--accent); }
.pub-estrella.int-2{ background:var(--raised); color:var(--tx-md); }
.pub-estrella.int-1{ background:transparent; color:var(--tx-lo); box-shadow:inset 0 0 0 1px var(--border); }
.pub-item:disabled{ opacity:.5; cursor:default; }

.pub-repite{
  border:1px solid var(--accent); border-radius:6px; padding:11px 12px;
  background:var(--acc-xlt); margin-bottom:10px;
}
.pub-item{
  display:block; width:100%; text-align:left; cursor:pointer;
  background:transparent; border:0; border-bottom:1px solid var(--border);
  padding:10px 12px; font:inherit; color:inherit;
}
.pub-item:last-child{ border-bottom:0; }
.pub-item:hover{ background:var(--acc-xlt); }
/* La elegida se marca con el filete lateral además del fondo: el fondo solo
   se pierde cuando la fila está en hover al lado. */
.pub-item.is-sel{ background:var(--acc-lt); box-shadow:inset 3px 0 0 var(--accent); }
.pub-item-t{ color:var(--tx-hi); font-weight:600; font-size:13px; line-height:1.35; }
.pub-item-m{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-top:6px; }
.pub-item-e{ color:var(--tx-lo); font-size:11.5px; text-transform:uppercase; letter-spacing:.04em; }

/* ── El aviso mientras se arma una publicación ──────────────────────
   Tapa la pantalla a propósito: mientras se renderizan las placas no hay
   nada que tocar, y sin esto parecía colgado. */
.pub-modal{
  position:fixed; inset:0; z-index:60;
  background:rgba(3,43,82,.42);
  display:flex; align-items:center; justify-content:center;
}
.pub-modal-caja{
  background:var(--surface); border:1px solid var(--border); border-radius:10px;
  box-shadow:0 18px 50px rgba(3,43,82,.28);
  padding:26px 30px; text-align:center; max-width:380px;
}
.pub-modal-spin{
  width:26px; height:26px; margin:0 auto 14px; border-radius:50%;
  border:3px solid var(--acc-lt); border-top-color:var(--accent);
  animation:pub-gira .8s linear infinite;
}
@keyframes pub-gira{ to{ transform:rotate(360deg); } }
@media (prefers-reduced-motion: reduce){ .pub-modal-spin{ animation-duration:2.4s; } }
.pub-modal-t{ color:var(--tx-hi); font-weight:700; font-size:15px; }
.pub-modal-s{ color:var(--accent); font-size:13.5px; margin-top:6px; }
.pub-modal-p{ color:var(--tx-lo); font-size:12.5px; margin-top:10px; }

/* Las placas, a la vista y en orden. El fondo gris deja ver el borde de las
   claras, que si no se confunden con la tarjeta del panel. */
.pub-placas{ display:flex; flex-wrap:wrap; gap:12px; margin-top:14px; }
.pub-placa{ position:relative; width:200px; }
.pub-placa img{
  display:block; width:100%; border-radius:4px; border:1px solid var(--border);
  background:var(--acc-xlt);
}
.pub-placa-falta{
  position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
  text-align:center; padding:10px; border-radius:4px; border:1px dashed var(--border-s);
  background:var(--surface); color:var(--tx-lo); font-size:12px;
}
.pub-placa-n{
  position:absolute; right:6px; bottom:6px; padding:1px 7px; border-radius:999px;
  background:rgba(3,43,82,.78); color:#fff; font-size:11px;
}
</style>
